import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Upload as UploadIcon,
  FileText,
  Sparkles,
  History,
  Loader2,
  Clock3,
  Database,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useI18n } from "../i18n/I18nProvider";
import { ingestFiles, type IngestionProgress } from "../services/ingestion";
import type { SourceFile, Transcript } from "../types";

interface UploadProps {
  onUpload: (payload: { transcripts: Transcript[]; sources: SourceFile[] }) => void;
  onDemo: () => void;
  onResume?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** index;
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatEta(seconds: number): string {
  if (seconds <= 0) return "< 1m";
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `~${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `~${hours}h ${rem}m` : `~${hours}h`;
}

export default function Upload({ onUpload, onDemo, onResume }: UploadProps) {
  const { t } = useI18n();
  const [isIngesting, setIsIngesting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [progress, setProgress] = useState<IngestionProgress | null>(null);
  const [sourcesPreview, setSourcesPreview] = useState<SourceFile[]>([]);

  const uploadPercent = useMemo(() => {
    if (!progress || progress.totalBytes <= 0) return 0;
    return Math.min(
      100,
      Math.round((progress.uploadedBytes / progress.totalBytes) * 100),
    );
  }, [progress]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setParseError(null);
      if (acceptedFiles.length === 0) return;

      try {
        setIsIngesting(true);
        setProgress(null);
        setSourcesPreview([]);

        const result = await ingestFiles(acceptedFiles, (next) => {
          setProgress(next);
        });

        setSourcesPreview(result.sources);

        if (result.transcripts.length === 0) {
          setParseError(
            "Files were uploaded, but no analyzable text was extracted yet. Text/PDF files are supported now; audio is archived for later transcription.",
          );
          return;
        }
        onUpload(result);
      } catch (err) {
        setParseError(
          err instanceof Error ? err.message : "Failed to read uploaded files",
        );
      } finally {
        setIsIngesting(false);
      }
    },
    [onUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/plain": [".txt"],
      "application/pdf": [".pdf"],
      "text/markdown": [".md"],
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
        ".pptx",
      ],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "audio/mpeg": [".mp3"],
      "audio/wav": [".wav"],
      "audio/x-m4a": [".m4a"],
      "audio/ogg": [".ogg"],
      "audio/webm": [".webm"],
    },
  });

  return (
    <div className="w-full max-w-2xl">
      <header className="mb-12 text-center">
        <h1 className="serif text-ink mb-4 text-6xl font-light tracking-tighter italic">
          {t("upload.title")}
        </h1>
        <p className="text-lg font-light text-zinc-700">{t("upload.tagline")}</p>
      </header>

      <div
        {...getRootProps()}
        role="button"
        tabIndex={0}
        aria-label={t("upload.drop")}
        aria-busy={isIngesting}
        className={cn(
          "group relative cursor-pointer transition-all duration-500",
          "flex flex-col items-center justify-center gap-6 rounded-[40px] border-2 border-dashed p-16",
          "focus-visible:ring-4 focus-visible:ring-emerald-500/30 focus-visible:outline-none",
          isDragActive
            ? "border-emerald-500/50 bg-emerald-500/5 backdrop-blur-xl"
            : "glass border-white/40 hover:bg-white/60",
        )}
      >
        <input {...getInputProps()} aria-label="Choose files" />

        <div
          className={cn(
            "flex h-20 w-20 items-center justify-center rounded-full transition-all duration-500",
            isDragActive
              ? "scale-110 bg-emerald-500 text-white"
              : "bg-zinc-100 text-zinc-400 group-hover:bg-zinc-200 group-hover:text-zinc-600",
          )}
        >
          {isIngesting ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : (
            <UploadIcon className="h-8 w-8" />
          )}
        </div>

        <div className="text-center">
          <p className="text-ink mb-2 text-xl font-medium">
            {isIngesting
              ? "Uploading and preparing sources..."
              : isDragActive
                ? t("upload.dropping")
                : t("upload.drop")}
          </p>
          <p className="text-sm text-zinc-700">
            Supports .txt, .md, .csv, .pdf, and audio files (.mp3, .wav, .m4a, .ogg).
          </p>
        </div>
      </div>

      {progress && (
        <section
          aria-live="polite"
          className="mt-6 space-y-4 rounded-3xl border border-emerald-100 bg-white/80 p-5"
        >
          <div className="flex items-center justify-between text-xs font-semibold tracking-wide text-zinc-600 uppercase">
            <span>Upload progress</span>
            <span>{uploadPercent}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${uploadPercent}%` }}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 text-sm text-zinc-700 md:grid-cols-3">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-emerald-600" />
              <span>
                {progress.completedFiles}/{progress.totalFiles} files completed
              </span>
            </div>
            <div className="flex items-center gap-2">
              <UploadIcon className="h-4 w-4 text-emerald-600" />
              <span>
                {formatBytes(progress.uploadedBytes)} /{" "}
                {formatBytes(progress.totalBytes)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-emerald-600" />
              <span>
                ETA upload {formatEta(progress.uploadEtaSeconds)} + analysis{" "}
                {formatEta(progress.analysisEtaSeconds)}
              </span>
            </div>
          </div>
          {progress.currentFileName && (
            <p className="truncate text-xs text-zinc-500">
              Processing: {progress.currentFileName}
            </p>
          )}
        </section>
      )}

      {parseError && (
        <p role="alert" className="mt-6 text-center text-sm text-rose-600">
          {parseError}
        </p>
      )}

      {sourcesPreview.length > 0 && (
        <section className="mt-6 rounded-3xl border border-zinc-200 bg-white/70 p-4">
          <h3 className="text-sm font-semibold tracking-wide text-zinc-600 uppercase">
            Archive preview
          </h3>
          <div className="mt-3 max-h-40 space-y-2 overflow-y-auto">
            {sourcesPreview.slice(0, 8).map((source) => (
              <div
                key={source.id}
                className="flex items-center justify-between rounded-xl border border-zinc-100 bg-white px-3 py-2 text-xs"
              >
                <span className="max-w-[65%] truncate text-zinc-700">
                  {source.name}
                </span>
                <span className="text-zinc-500">{source.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-8 flex flex-col items-center gap-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDemo();
          }}
          className="group flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium text-emerald-700 transition-all hover:text-emerald-800 focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:outline-none"
        >
          <Sparkles className="h-4 w-4 transition-transform group-hover:scale-110" />
          {t("upload.demoCta")}
        </button>

        {onResume && (
          <button
            type="button"
            onClick={onResume}
            className="group flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium text-zinc-500 transition-all hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-zinc-400/40 focus-visible:outline-none"
          >
            <History className="h-3 w-3" />
            {t("upload.resumeCta")}
          </button>
        )}
      </div>

      <div
        aria-hidden="true"
        className="mt-12 grid grid-cols-3 gap-4 opacity-30 contrast-75 grayscale"
      >
        <div className="flex h-24 items-center justify-center rounded-2xl border border-zinc-200 bg-white">
          <FileText className="h-6 w-6 text-zinc-400" />
        </div>
        <div className="flex h-24 items-center justify-center rounded-2xl border border-zinc-200 bg-white">
          <FileText className="h-6 w-6 text-zinc-400" />
        </div>
        <div className="flex h-24 items-center justify-center rounded-2xl border border-zinc-200 bg-white">
          <FileText className="h-6 w-6 text-zinc-400" />
        </div>
      </div>
    </div>
  );
}
